import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { Box, Button, Divider, Grid, Progress, SectionHeader, Typography } from 'web-library';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  selectApplicationUuid,
  selectBankTransferOption,
  selectCandidateSelectedSubjects,
} from '../../../../store/resitSlice';
import ContactDetails from '../../../../components/ContactDetails';
import Declaration from '../../../../components/Declaration';
import PersonalDetails from '../../../../components/PersonalDetails';
import SelectedSubjectReview from '../../../../components/SelectedSubjectsReview';
import PaymentButton, { PaymentMethod } from '../../../payment/components/PaymentButton';
import { useResitNavigation } from '../../../../routes/ResitRoute';
import RouteKey from '../../../../routes/key';
import { useTriggerMutate } from '../../../../hooks/query';
import { fallbackEmptyString, getNullIfEmptyString } from '../../../../utils/StringUtils';
import { usePutCandidateProfileAndResit } from '../../../../api-refactored/resit-registration/usePutCandidateProfileAndResit';
import { usePostConfirmAndPay } from '../../../../api-refactored/resit-registration/usePostConfirmAndPay';
import { useGetPaymentAccess } from '../../../../api-refactored/financial/useGetPaymentAccess';

const { receipt } = RouteKey.resit.child;
const { resit } = RouteKey.child;

const useStyles = makeStyles()((theme) =>
({
  root: {},
  container: {
    alignItems: 'center',
  },
  buttonContainer: {
    padding: theme.spacing(1),
  },
  btnContainer: {
    '& .MuiButtonBase-root': {
      [theme.breakpoints.down('md')]: {
        width: '100%',
        marginBottom: theme.spacing(2),
      },
    },
  },
  txtError: {
    color: 'red',
  },
  btnRoot: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  submitButtonContainer: {
    width: '100%',
  },
}));

const ReviewAndPay = ({ previous }: { previous: (subjects: number[]) => void }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const [isAgreed, setIsAgreed] = useState(false);
  const [agreementError, setAgreementError] = useState(false);
  const [isPaymentDisabled, setIsPaymentDisabled] = useState(false);

  const [paymentAccessDetailsOption, setPaymentAccessDetailsOption] = useState<
    Array<PaymentMethod>
  >([]);

  const bankTransferEnabled = useSelector(selectBankTransferOption);

  useGetPaymentAccess({
    onSuccess: (res) => {
      const paymentAccessDetailsArray = [];
      if (res.bankTransferMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.BANK_TRANSFER || bankTransferEnabled);
      }
      if (res.creditCardMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.ENETS_CREDIT_CARD);
      }
      if (res.payNowMethod) {
        paymentAccessDetailsArray.push(PaymentMethod.PAYNOW_QR);
      }
      setPaymentAccessDetailsOption(paymentAccessDetailsArray);
    },
  });

  const { accessToken } = useResitNavigation();

  /** candidate selected subjects
   * */
  const candidateSelectedSubject = useSelector(selectCandidateSelectedSubjects);
  const candidateApplicationUuid = useSelector(selectApplicationUuid);

  /** to fetch candidate selected subjects and particular info
   * */
  const { mutate: postCandidateProfileAndResit, data } = usePutCandidateProfileAndResit();

  /** to save candidate after payment is successful
   * */
  const registerCandidateResponse = usePostConfirmAndPay();

  /** to handle user declaration agreement
   * */
  const handleDeclarationAgreement = async () => {
    setAgreementError(false);
    setIsAgreed(!isAgreed);
  };

  /** ALEX TEST to handle user click payment button
   * */
  const handleClickPaymentButtonTest = (callback: () => void) => {
    if (!isAgreed) {
      setAgreementError(true);
    } else if (Array.isArray(candidateSelectedSubject)) {
      callback();
    }
  };

  /** ALEX TEST to handle payment option
   * */
  const handlePaymentOptionTest = (
    callback: (internalTransactionRef: string) => void,
    method?: string
  ) => {
    registerCandidateResponse.mutate(
      {
        uuid: candidateApplicationUuid,
        resitSubjects: {
          // @ts-ignore
          subjects: candidateSelectedSubject,
          accessLinkToken: getNullIfEmptyString(accessToken),
          paymentMode: fallbackEmptyString(method),
        },
      },
      {
        onSuccess: (registerResponse) => {
          if (registerResponse.paymentSuccess) {
            history.push(
              `/${resit}/resit/${receipt.route}?internalTransactionRef=${registerResponse.internalTransactionRef}`,
              candidateApplicationUuid
            );
          }

          callback(registerResponse.internalTransactionRef);
        },
        onError: () => {
          callback('');
        },
      }
    );
  };

  /** changed to use trigger mutate cuz refetching after do some changes in mutate
   * */
  const callMutation = useCallback(() => {
    if (Array.isArray(candidateSelectedSubject) && candidateSelectedSubject.length > 0) {
      postCandidateProfileAndResit({
        uuid: candidateApplicationUuid,
        // @ts-ignore
        resitSubjects: candidateSelectedSubject,
      });
    }
  }, [candidateSelectedSubject, postCandidateProfileAndResit, candidateApplicationUuid]);

  const setTriggerMutate = useTriggerMutate(callMutation);

  useEffect(() => {
    if (Array.isArray(candidateSelectedSubject) && candidateSelectedSubject.length > 0) {
      setTriggerMutate((prev) => prev + 1);
    } else if (!Array.isArray(candidateSelectedSubject) || candidateSelectedSubject.length <= 0) {
      previous(candidateSelectedSubject.map((s, i) => i));
    }
  }, [candidateSelectedSubject, previous, setTriggerMutate]);

  const redirectToReceiptPage = (txnId: string) =>
    history.push(
      `/${resit}/resit/${receipt.route}?internalTransactionRef=${txnId}`,
      candidateApplicationUuid
    );

  if (!data) {
    return <Progress color="secondary" />;
  }

  return (
    <Box className={classes.container}>
      <SectionHeader title="Review & Pay" />
      <Grid container>
        <Grid xs={12}>
          <PersonalDetails
            statutoryName={data?.candidateDetails.statutoryName}
            hanyuPinyinName={data?.candidateDetails.hanyuPinyinName}
            nricFin={data?.candidateDetails.nricFin}
            nationality={data?.candidateDetails.nationality}
            citizenship={data?.candidateDetails.citizenship}
            schoolName={data?.candidateDetails.schoolName}
          />
        </Grid>
      </Grid>
      <Divider />
      <Grid container>
        <Grid xs={12}>
          <ContactDetails
            contactDetails={{
              email: data?.candidateDetails.email || '',
              mobileNumber: data?.candidateDetails.mobileNumber,
              phoneNumber: data?.candidateDetails.phoneNumber,
              mailingAddress: data?.candidateDetails.mailingAddress,
              foreignAddress1: data?.candidateDetails.foreignAddress1,
              foreignAddress2: data?.candidateDetails.foreignAddress2,
              foreignAddress3: data?.candidateDetails.foreignAddress3,
            }}
            editable={false}
          />
        </Grid>
      </Grid>
      <Divider />
      <Grid container spacing={3}>
        <Grid xs={12}>
          <SelectedSubjectReview
            examLevel={data?.subjectSummary.examLevel}
            examYear={data?.subjectSummary.examYear}
            candidateSubjects={data?.subjectSummary.subjectInfo}
            gstRegNo={data?.subjectSummary.gstRegNo}
            totalExamFee={data?.subjectSummary.totalExamFee}
            totalFee={data?.subjectSummary.totalFee}
            totalGst={data?.subjectSummary.totalGst}
            totalPayable={data?.subjectSummary.totalPayable}
            gstAbsorbed={data?.subjectSummary.gstAbsorbed}
          />
        </Grid>
        <Grid xs={12}>
          <Declaration
            title="Declaration"
            isAgreed={isAgreed}
            handleAgreement={handleDeclarationAgreement}
          />
        </Grid>
        {agreementError && (
          <Grid xs={12} md={8}>
            <Typography className={classes.txtError} display="inline" variant="body2">
              {t('components->>Declaration.Please confirm declaration before you proceed.')}
            </Typography>
          </Grid>
        )}
        <Grid container xs={12} sm={12} md={10} lg={8} spacing={2}>
          <Grid xs={12} sm={12} md={3} lg={4}>
            <Button
              id="button-reviewPay-back"
              fullWidth
              className={classes.btnRoot}
              variant="outlined"
              onClick={() => {
                previous(candidateSelectedSubject.map((s, i) => i));
              }}
            >
              {t('resit->>RegisterResit.Back')}
            </Button>
          </Grid>
          {data?.subjectSummary.totalExamFee && data?.subjectSummary.totalExamFee !== '0.00' ? (
            <Grid xs={12} sm={12} md={5} lg={5}>
              <PaymentButton
                buttonClassName={classes.btnRoot}
                isUniqueLink={false}
                disabled={isPaymentDisabled}
                onPaymentComplete={(isRetry) => setIsPaymentDisabled(isRetry)}
                paymentMethodsAvailable={paymentAccessDetailsOption}
                onClickPaymentButton={(callback) => {
                  handleClickPaymentButtonTest(callback);
                }}
                onHandlePaymentMethod={(callback, method) => {
                  handlePaymentOptionTest(callback, method);
                }}
                onSuccessPayment={redirectToReceiptPage}
              />
            </Grid>
          ) : (
            <Grid xs={12} sm={12} md={5} lg={5}>
              <Button
                id="button-reviewPay-confirmRegistration"
                fullWidth
                onClick={() => {
                  handleClickPaymentButtonTest(() => {
                    handlePaymentOptionTest(redirectToReceiptPage);
                  });
                }}
              >
                {t('resit->>RegisterResit.Confirm Registration')}
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
export default ReviewAndPay;
